import { Link } from 'gatsby';
import type * as React from 'react';

import { useLanguage } from '../../context/LanguageContext';
import styles from './LanguageSwitcher.module.css';

export interface LanguageSwitcherProps {
  /** Override the language from context (optional) */
  lang?: 'de' | 'en';
  /** Override the alternate slug from context (optional) */
  alternateSlug?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * LanguageSwitcher component.
 *
 * Displays a link to the equivalent page in the other language.
 * Shows "EN" when current language is German (to switch to English),
 * and "DE" when current language is English (to switch to German).
 *
 * Uses Gatsby's Link component for client-side navigation.
 * Accessible with descriptive aria-label.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  lang: langProp,
  alternateSlug: alternateSlugProp,
  className,
}) => {
  const { lang: contextLang, alternateSlug: contextAlternateSlug } = useLanguage();

  const lang = langProp ?? contextLang;
  const alternateSlug = alternateSlugProp ?? contextAlternateSlug;

  const label = lang === 'de' ? 'EN' : 'DE';
  const ariaLabel = lang === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln';

  return (
    <span className={`${styles.switcher}${className ? ` ${className}` : ''}`}>
      <Link
        to={alternateSlug}
        className={styles.link}
        aria-label={ariaLabel}
        hrefLang={lang === 'de' ? 'en' : 'de'}
      >
        {label}
      </Link>
    </span>
  );
};

export default LanguageSwitcher;
